"use client";

// Reports reel playback to Screen9's Stats API (dev.screen9.com/doc/statsapi),
// the same public endpoint Quickchannel's own Amber player reports to — so
// reels played through our native <video> elements still count as views in
// Quickchannel's dashboard. Best-effort throughout: a failure here must never
// affect playback.

const STATS_ENDPOINT = "https://statsapi.screen9.com/sessions";
const UID_STORAGE_KEY = "screen9_uid";
const WATCHED_INTERVAL_SECONDS = 15;

function getUid(): string | undefined {
  try {
    let uid = localStorage.getItem(UID_STORAGE_KEY);
    if (!uid) {
      uid = crypto.randomUUID();
      localStorage.setItem(UID_STORAGE_KEY, uid);
    }
    return uid;
  } catch {
    return undefined;
  }
}

type Screen9Event = {
  event: string;
  mediatime?: number;
  value?: number;
};

async function createSession(token: string): Promise<string | null> {
  try {
    const res = await fetch(STATS_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token,
        display: true,
        page: window.location.href,
        uid: getUid(),
      }),
    });
    const data = await res.json();
    return data?.sessionid ?? null;
  } catch {
    return null;
  }
}

async function postEvents(
  sessionId: string,
  mediaid: string,
  order: number,
  events: Screen9Event[],
): Promise<void> {
  try {
    await fetch(`${STATS_ENDPOINT}/${sessionId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ order, mediaid, events }),
    });
  } catch {
    // Ignore — stats reporting is best-effort.
  }
}

export class ReelStatsTracker {
  private token: string;
  private mediaid: string;
  private sessionId: string | null = null;
  private sessionPromise: Promise<string | null> | null = null;
  private order = 0;
  private lastWatchedAt = 0;
  private started = false;

  constructor(token: string, mediaid: string) {
    this.token = token;
    this.mediaid = mediaid;
  }

  private ensureSession(): Promise<string | null> {
    if (this.sessionId) return Promise.resolve(this.sessionId);
    if (!this.sessionPromise) {
      this.sessionPromise = createSession(this.token).then((id) => {
        this.sessionId = id;
        return id;
      });
    }
    return this.sessionPromise;
  }

  private async send(events: Screen9Event[]): Promise<void> {
    const sessionId = await this.ensureSession();
    if (!sessionId) return;
    this.order += 1;
    await postEvents(sessionId, this.mediaid, this.order, events);
  }

  onPlay(currentTime: number): void {
    this.started = true;
    this.lastWatchedAt = currentTime;
    void this.send([{ event: "control_play", mediatime: currentTime, value: currentTime }]);
  }

  onTimeUpdate(currentTime: number): void {
    if (!this.started) return;
    if (currentTime - this.lastWatchedAt < WATCHED_INTERVAL_SECONDS) return;
    this.lastWatchedAt = currentTime;
    void this.send([{ event: "base_watched", mediatime: currentTime }]);
  }

  onPause(currentTime: number): void {
    if (!this.started) return;
    void this.send([{ event: "control_pause", mediatime: currentTime, value: currentTime }]);
  }

  finish(): void {
    if (!this.started) return;
    this.started = false;
    void this.send([{ event: "base_finish" }]).then(() => {
      this.sessionId = null;
      this.sessionPromise = null;
      this.order = 0;
    });
  }
}
